/** Modèle de rôles partagé backend/frontend. */

import { CATEGORY_KEYS, SERVICE_BY_CATEGORY, type CategoryKey, type ServiceKey } from './catalog.js';

/**
 * Identité résolue à la connexion.
 * - `services` vient des groupes Entra (vide = pas d'administrateur de service).
 * - `isManager` vient de la hiérarchie La Bécane, et se **cumule** avec `services`.
 */
export interface SessionUser {
  id: number;
  email: string;
  displayName: string;
  /** Object id Microsoft. */
  oid?: string;
  services: ServiceKey[];
  isManager: boolean;
  /** Ids Bécane des sites de rattachement. */
  siteIds: number[];
  /**
   * Ids Bécane des sites dont l'utilisateur est référent (encadrement opérationnel).
   * Distinct de `siteIds` : on est souvent référent d'un site auquel on n'est pas rattaché.
   */
  refereeSiteIds: number[];
  managerEmail?: string;
}

/**
 * Personnel de terrain, rattaché à des sites : concierges et techniciens extérieurs.
 *
 * Ce seul ensemble porte deux règles de sens contraire, et c'est voulu :
 * il **restreint** (on ne déclare que sur ses propres sites) et il **délègue**
 * (un administrateur de service déclare à sa place, cf. `canDeclareFor`).
 */
export const FIELD_STAFF_ROLES = ['CONC', 'TEREXT'] as const;

/** Un collaborateur dont le matériel peut être déclaré par un administrateur de service. */
export function isFieldStaff(roles: readonly string[]): boolean {
  return roles.some((role) => (FIELD_STAFF_ROLES as readonly string[]).includes(role));
}

/** Un administrateur de service accède au tableau de bord et à l'inventaire. */
export function isServiceAdmin(user: Pick<SessionUser, 'services'>): boolean {
  return user.services.length > 0;
}

/** Le back-office est ouvert aux administrateurs de service **et** aux managers. */
export function canAccessBackOffice(user: Pick<SessionUser, 'services' | 'isManager'>): boolean {
  return isServiceAdmin(user) || user.isManager;
}

/** Un service n'approuve que les demandes portant sur les catégories dont il a la charge. */
export function canApproveService(
  user: Pick<SessionUser, 'services'>,
  service: ServiceKey,
): boolean {
  return user.services.includes(service);
}

/** Catégories relevant d'un service donné (ex. SI → INFO, NET). */
export function categoriesForService(service: ServiceKey): CategoryKey[] {
  return CATEGORY_KEYS.filter((category) => SERVICE_BY_CATEGORY[category] === service);
}

/**
 * Le SI est l'administrateur technique de l'application : il agit sur **tout** le matériel,
 * quelle que soit la catégorie. QPL et RH n'agissent que sur la leur.
 *
 * L'asymétrie est volontaire et invisible dans `SERVICE_BY_CATEGORY`, qui suggère au
 * contraire trois périmètres symétriques.
 */
const UNRESTRICTED_SERVICE: ServiceKey = 'SI';

/**
 * Un administrateur **consulte** tout l'inventaire, quel que soit son service : la recherche,
 * les trois axes et l'export n'ont d'intérêt que sur le parc entier.
 */
export function canViewInventory(user: Pick<SessionUser, 'services'>): boolean {
  return isServiceAdmin(user);
}

/**
 * Un administrateur n'**agit** (modifier, transférer, restituer, mettre au rebut, déclarer
 * pour un tiers) que sur les catégories de son service — sauf le SI, qui agit sur tout.
 *
 * Contrôlé côté serveur sur chaque route d'écriture : l'UI se contente de ne pas proposer
 * ce qui sera refusé.
 */
export function canActOnCategory(
  user: Pick<SessionUser, 'services'>,
  category: CategoryKey,
): boolean {
  if (user.services.includes(UNRESTRICTED_SERVICE)) return true;
  return user.services.includes(SERVICE_BY_CATEGORY[category]);
}

/** Catégories sur lesquelles cet administrateur peut agir. Vide s'il n'est pas administrateur. */
export function actionableCategories(user: Pick<SessionUser, 'services'>): CategoryKey[] {
  return CATEGORY_KEYS.filter((category) => canActOnCategory(user, category));
}

// ---------------------------------------------------------------------------
// Référent de site
// ---------------------------------------------------------------------------

/** L'utilisateur est-il référent de ce site ? Un matériel sans site ne l'est de personne. */
export function isSiteReferee(
  user: Pick<SessionUser, 'refereeSiteIds'>,
  becaneSiteId: number | null,
): boolean {
  return becaneSiteId !== null && user.refereeSiteIds.includes(becaneSiteId);
}

/** Le rattachement d'un matériel, tel qu'il détermine qui peut agir dessus. */
export interface EquipmentScope {
  categoryKey: CategoryKey;
  becaneSiteId: number | null;
}

/**
 * Droit d'agir sur **un** matériel donné, par l'une ou l'autre de deux voies indépendantes :
 * la catégorie (administrateur de service) ou le site (référent d'encadrement).
 *
 * Le référent l'emporte sur le découpage par service : il voit et manie tout le matériel de
 * ses sites, y compris le matériel adapté qui relève des RH.
 */
export function canActOnEquipment(
  user: Pick<SessionUser, 'services' | 'refereeSiteIds'>,
  equipment: EquipmentScope,
): boolean {
  return isSiteReferee(user, equipment.becaneSiteId) || canActOnCategory(user, equipment.categoryKey);
}

// ---------------------------------------------------------------------------
// Déclarer pour le compte d'un tiers
// ---------------------------------------------------------------------------

/** Le collaborateur au nom de qui on déclare, tel que La Bécane le décrit. */
export interface DeclarationBeneficiary {
  email: string;
  /** Codes de rôle Bécane (`CONC`, `OPE`, `FRCH`…). */
  roles: string[];
  /** E-mail du manager hiérarchique, selon La Bécane. */
  managerEmail?: string | null;
}

/**
 * Qui peut déclarer du matériel au nom de qui.
 *
 * Quatre voies, dans l'ordre où elles se lisent :
 *   - soi-même, toujours ;
 *   - le SI, qui a la main sur tout ;
 *   - un manager, pour ses collaborateurs directs ;
 *   - un administrateur de service (QPL, RH), pour le personnel de terrain — et alors sur
 *     **n'importe quelle catégorie**, `canActOnCategory` ne s'applique pas ici : on saisit
 *     l'inventaire d'un concierge d'un bloc, pas famille par famille.
 *
 * Être référent d'un site n'ouvre pas ce droit : il porte sur le matériel d'un site, pas sur
 * l'identité des collaborateurs qui y travaillent.
 */
export function canDeclareFor(
  user: Pick<SessionUser, 'email' | 'services' | 'isManager'>,
  target: DeclarationBeneficiary,
): boolean {
  const actorEmail = user.email.toLowerCase();
  if (target.email.toLowerCase() === actorEmail) return true;
  if (user.services.includes(UNRESTRICTED_SERVICE)) return true;
  if (user.isManager && target.managerEmail?.toLowerCase() === actorEmail) return true;
  if (isServiceAdmin(user) && isFieldStaff(target.roles)) return true;
  return false;
}
